/** IST calendar-day helpers. Days are 'YYYY-MM-DD' strings; arithmetic is on the civil calendar, never across zones. */

const IST_OFFSET_MS = 330 * 60 * 1000;
const DAY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

function parse(day: string): { y: number; m: number; d: number } {
  const match = DAY_RE.exec(day);
  if (!match) throw new Error(`Not a YYYY-MM-DD day: "${day}"`);
  const y = Number(match[1]);
  const m = Number(match[2]);
  const d = Number(match[3]);
  if (m < 1 || m > 12 || d < 1 || d > daysInMonth(y, m)) throw new Error(`Not a real day: "${day}"`);
  return { y, m, d };
}

function isLeap(y: number): boolean {
  return (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;
}

function daysInMonth(y: number, m: number): number {
  return [31, isLeap(y) ? 29 : 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][m - 1]!;
}

function fmt(y: number, m: number, d: number): string {
  return `${String(y).padStart(4, '0')}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

/** The IST calendar day of an ISO instant (e.g. 2026-10-31T19:00:00Z -> 2026-11-01). */
export function istDateOf(isoInstant: string): string {
  const t = Date.parse(isoInstant);
  if (Number.isNaN(t)) throw new Error(`Not an ISO instant: "${isoInstant}"`);
  return new Date(t + IST_OFFSET_MS).toISOString().slice(0, 10);
}

export function monthStart(day: string): string {
  const { y, m } = parse(day);
  return fmt(y, m, 1);
}

export function monthEnd(day: string): string {
  const { y, m } = parse(day);
  return fmt(y, m, daysInMonth(y, m));
}

export function addDaysIso(day: string, n: number): string {
  if (!Number.isInteger(n)) throw new Error(`Days must be an integer, got ${n}`);
  const { y, m, d } = parse(day);
  // Date.UTC on a civil date: no zone involved.
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
}

/** The due date of the bill for `month` (YYYY-MM-01): the dueDay of the NEXT month (bill 1st, due 3rd by default). */
export function dueDateOf(month: string, dueDay = 3): string {
  const { y, m } = parse(month);
  if (!Number.isInteger(dueDay) || dueDay < 1 || dueDay > 28) throw new Error(`Due day must be 1..28, got ${dueDay}`);
  return m === 12 ? fmt(y + 1, 1, dueDay) : fmt(y, m + 1, dueDay);
}
